/**
 * AboutTabPanel — Mini-Project About tab.
 *
 * Migration 167 unification (2026-05-19, Skip's call):
 * About content for Mini-Project AND Full Project now share the same
 * backend table (project_content_sections, polymorphic via entity_type)
 * AND the same frontend component (ProjectContentSections). One code
 * path, one storage path, one set of bugs to fix. Future @mentions for
 * enablers / Global Solutions / Global Problems and full-text search
 * get implemented once.
 *
 * Spec §11.1 amendment (implicit by migration 167): Mini-Project About
 * sections are now dynamic — Lead can add / delete / reorder. The 5
 * canonical sections (Overview / Methodologies / Best Practices /
 * Risks / Lessons Learned) are seeded as defaults at instantiation,
 * not enforced as hard constraints. File a separate ticket if reverting
 * to constrained mode is preferred.
 *
 * This panel's remaining responsibilities (separate from the unified
 * sections renderer):
 *   1. Short description at top — Mini-Project-specific concept.
 *   2. Team accordion — Mini-Project-specific (Skip's 2026-05-19 decision
 *      to relocate Team from Progress to About).
 *   3. Edit Page / Done Editing toggle — controls the unified component's
 *      isEditing state.
 *   4. Delegate to <ProjectContentSections entityType="mini_project" ...>
 *      for the actual About content rendering.
 *
 * Strings localized via `pages:miniProject.about.*` (Chunk D Phase 5
 * sweep, 2026-05-22).
 */

import { useCallback, useEffect, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchMiniProjectTeamMembers,
  fetchMiniProjectEnablerLinks,
  type MiniProject,
  type MiniProjectTeamMember,
  type MiniProjectEnablerLink,
} from "../../api/miniProjects";
import TeamSection from "./sections/TeamSection";
import EnablersSection from "./sections/EnablersSection";
import ProjectContentSections from "../../Components/ProjectContentSections/ProjectContentSections";

interface Props {
  miniProject: MiniProject;
  isLead: boolean;
  currentUserGuid: string;
  onMutated: () => void;
}

/* ------------------------------------------------------------------ */
/*  Visual tokens                                                      */
/* ------------------------------------------------------------------ */

// Width inherits from PillarPageShell.tabAreaStyle (1620px cap), matching
// Full Project's About tab and Community's Alignment tab. No additional
// panel-level cap — the shell controls the outer bound.
const panelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  // Match Full Project's About tab rhythm (CoreTabPanel): tight top
  // padding and a 24px inter-block gap, instead of the looser 16/28 that
  // pushed the short description and Overview too far apart.
  gap: 24,
  padding: "6px 6px",
  inlineSize: "100%",
};

const editToggleRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  // The panel's 24px flex gap is the right body rhythm (matches Full
  // Project), but it makes the Edit Page row sit too far above the
  // description. Full Project's toggle hugs the description at ~16px
  // (block-flow margin collapse). Pull this row down 8px so the gap to
  // the description is 24 − 8 = 16, matching Full Project, without
  // affecting the 24px spacing between the lower blocks.
  marginBlockEnd: -8,
};

const editToggleBtnStyle: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--border-subtle, var(--border-default))",
  borderRadius: 6,
  color: "var(--text-muted)",
  fontSize: 12,
  padding: "4px 14px",
  cursor: "pointer",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: "var(--text-primary)",
};

// Skip 2026-05-20 — no box around Team cards. Matches Community Alignment
// tab's "Key Community Leaders" pattern (section heading + cards directly
// below, no surrounding border or background).

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AboutTabPanel({ miniProject, isLead, currentUserGuid, onMutated }: Props) {
  const { t } = useAppTranslation("pages");
  const [isEditingAbout, setIsEditingAbout] = useState(false);

  // Team — relocated from Progress to About (Skip's 2026-05-19 decision).
  // Local fetch keeps this panel independent of Progress tab data flow.
  const [teamMembers, setTeamMembers] = useState<MiniProjectTeamMember[]>([]);
  const refetchTeam = useCallback(async () => {
    try {
      const tm = await fetchMiniProjectTeamMembers(miniProject.miniProjectGuid);
      setTeamMembers(tm);
    } catch {
      setTeamMembers([]);
    }
  }, [miniProject.miniProjectGuid]);
  useEffect(() => { refetchTeam(); }, [refetchTeam]);

  // Enablers — relocated from Progress to About (DEV-1080, Chunk B).
  // Same self-contained-fetch pattern as Team to avoid coupling tabs.
  const [enablerLinks, setEnablerLinks] = useState<MiniProjectEnablerLink[]>([]);
  const refetchEnablers = useCallback(async () => {
    try {
      const links = await fetchMiniProjectEnablerLinks(miniProject.miniProjectGuid);
      setEnablerLinks(links);
    } catch {
      setEnablerLinks([]);
    }
  }, [miniProject.miniProjectGuid]);
  useEffect(() => { refetchEnablers(); }, [refetchEnablers]);

  // Enabler add-form state, lifted here so the "+" icon next to the
  // section heading can trigger it (DEV-1081 Chunk D).
  const [addingEnabler, setAddingEnabler] = useState(false);

  // Short description — card blurb shown at the top. Editing happens in
  // the Settings modal (gear icon in the header), not inline here.
  const shortDesc = miniProject.shortDescription?.trim() || "";

  // onMutated is reserved for future cross-cutting refetches (e.g. when
  // sections change and the page-level KPI summary should refresh). The
  // unified ProjectContentSections handles its own queryCache invalidation
  // so we don't need to chain to it for sections-only writes.
  void onMutated;

  return (
    <div style={panelStyle}>
      {/* ── Edit Page toggle (Lead-only) — sits above all About content ── */}
      {isLead && (
        <div style={editToggleRowStyle}>
          <button
            type="button"
            style={editToggleBtnStyle}
            onClick={() => setIsEditingAbout((v) => !v)}
          >
            {isEditingAbout ? t("miniProject.about.doneEditing") : t("miniProject.about.editPage")}
          </button>
        </div>
      )}

      {/* ── Short Description ── */}
      {shortDesc ? (
        <section
          style={{
            padding: "14px 18px",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border-default)",
            borderInlineStart: "4px solid var(--pillar-accent, var(--accent-border, #6B7280))",
            borderRadius: 12,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.6,
              color: "var(--text-secondary)",
              whiteSpace: "pre-wrap",
            }}
          >
            {shortDesc}
          </p>
        </section>
      ) : isLead ? (
        <section
          style={{
            padding: "10px 14px",
            background: "var(--bg-surface)",
            border: "1px dashed var(--border-default)",
            borderRadius: 12,
            fontSize: 12,
            color: "var(--text-muted)",
            fontStyle: "italic",
          }}
        >
          {t("miniProject.about.shortDescEmpty")}
        </section>
      ) : null}

      {/* ── Overview ──
          Lead narrative, pinned above Team via the shared component's
          overview slot (typed section_type='overview'). Renders nothing
          until an Overview is added from the Background block below. */}
      <ProjectContentSections
        entityType="mini_project"
        entityGuid={miniProject.miniProjectGuid}
        canEdit={isLead}
        isEditing={isEditingAbout}
        slot="overview"
      />

      {/* ── Team ──
          No surrounding box — matches Community Alignment tab's
          "Key Community Leaders" pattern. Heading sits directly above
          the cards row. */}
      <section>
        <h4 style={{ ...sectionTitleStyle, marginBlockEnd: 12 }}>
          {t("miniProject.about.section.team")}
          <span style={{ marginInlineStart: 8, fontSize: 12, color: "var(--text-muted)", fontWeight: 400 }}>
            {teamMembers.filter((m) => !m.leftAt).length === 1
              ? t("miniProject.about.team.memberOne")
              : t("miniProject.about.team.memberPlural", { count: teamMembers.filter((m) => !m.leftAt).length })}
          </span>
        </h4>
        <TeamSection
          miniProjectGuid={miniProject.miniProjectGuid}
          teamMembers={teamMembers}
          isLead={isLead}
          currentUserGuid={currentUserGuid}
          onMutated={refetchTeam}
        />
      </section>

      {/* ── Enablers ──
          DEV-1080, Chunk B — relocated from Progress to About per Skip's
          2026-05-22 decision. Same mini-flip-card pattern as Team.
          DEV-1081 Chunk D: "+" icon next to the heading triggers the
          add form, matching the SectionPanel pattern on the Progress
          tab. */}
      <section>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBlockEnd: 12,
          }}
        >
          <h4 style={{ ...sectionTitleStyle, margin: 0 }}>
            {t("miniProject.about.section.enablers")}
            <span
              style={{
                marginInlineStart: 8,
                fontSize: 12,
                color: "var(--text-muted)",
                fontWeight: 400,
              }}
            >
              {enablerLinks.length === 1
                ? t("miniProject.about.enablers.contactOne")
                : t("miniProject.about.enablers.contactPlural", { count: enablerLinks.length })}
            </span>
          </h4>
          {isLead && (
            <button
              type="button"
              onClick={() => setAddingEnabler(true)}
              aria-label={t("miniProject.about.enablers.addAria")}
              title={t("miniProject.about.enablers.addTitle")}
              style={{
                inlineSize: 24,
                blockSize: 24,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "0.5px solid var(--border-default)",
                color: "var(--text-secondary)",
                cursor: "pointer",
                borderRadius: "50%",
                padding: 0,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}
        </div>
        <EnablersSection
          miniProjectGuid={miniProject.miniProjectGuid}
          enablerLinks={enablerLinks}
          isLead={isLead}
          onMutated={refetchEnablers}
          addOpen={addingEnabler}
          onCloseAdd={() => setAddingEnabler(false)}
        />
      </section>

      {/* ── Geography ──
          DEV-1080, Chunk B — relocated from Progress to About. Geography
          is a project attribute (where), not a tracking signal. Stays a
          simple text display in v1; a map utility is queued (DEV-1078). */}
      <section>
        <h4 style={{ ...sectionTitleStyle, marginBlockEnd: 8 }}>
          {t("miniProject.about.section.geography")}
        </h4>
        {miniProject.locationText ? (
          <p style={{ margin: 0, fontSize: 14, color: "var(--text-primary)" }}>
            {miniProject.locationText}
          </p>
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontStyle: "italic",
              color: "var(--text-muted)",
            }}
          >
            {miniProject.locationGeographyGuid
              ? t("miniProject.about.geography.customSet")
              : t("miniProject.about.geography.inheriting")}
          </p>
        )}
      </section>

      {/* ── About content sections (unified backend per migration 167) ── */}
      {/* DEV-1264: the retrospective is a section here. The affordance
          (write-prompt + distinct treatment) turns on once the project
          is complete and the viewer is the Lead. */}
      <ProjectContentSections
        entityType="mini_project"
        entityGuid={miniProject.miniProjectGuid}
        canEdit={isLead}
        isEditing={isEditingAbout}
        slot="body"
        retrospectiveAffordance={miniProject.status === "complete" && isLead}
      />
    </div>
  );
}
